import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown } from 'lucide-react';

const isOption = (child) => React.isValidElement(child) && child.type === 'option';

const Select = ({ label, error, className = '', children, value, name, onChange, disabled, required, ...props }) => {
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const ref = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);
  const reactId = useId().replace(/:/g, '');
  const labelId = `select-${reactId}-label`;
  const listId = props.id ? `${props.id}-listbox` : `select-${reactId}-listbox`;
  const options = useMemo(() => React.Children.toArray(children).filter(isOption), [children]);
  const selectedIndex = options.findIndex((option) => String(option.props.value) === String(value));
  const selected = options[selectedIndex];
  const hasValue = value !== '' && value !== null && value !== undefined;
  const enabledIndexes = useMemo(() => options.map((option, index) => option.props.disabled ? -1 : index).filter((index) => index >= 0), [options]);

  useEffect(() => {
    const close = (event) => {
      if (ref.current && !ref.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  useEffect(() => {
    if (!open) return;
    const fallback = enabledIndexes[0] ?? -1;
    setActiveIndex(options[selectedIndex]?.props.disabled ? fallback : (selectedIndex >= 0 ? selectedIndex : fallback));
    listRef.current?.focus();
  }, [enabledIndexes, open, options, selectedIndex]);

  const close = ({ focusTrigger = false } = {}) => {
    setOpen(false);
    if (focusTrigger) requestAnimationFrame(() => triggerRef.current?.focus());
  };

  const select = (nextValue) => {
    onChange?.({ target: { name: name || '', value: String(nextValue) } });
    close({ focusTrigger: true });
  };

  const moveActive = (direction) => {
    if (!enabledIndexes.length) return;
    const current = enabledIndexes.indexOf(activeIndex);
    const next = enabledIndexes[(current + direction + enabledIndexes.length) % enabledIndexes.length];
    setActiveIndex(next);
  };

  const handleKeyDown = (event) => {
    if (disabled) return;
    if (!open && ['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      return;
    }
    if (!open) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); moveActive(1); }
    else if (event.key === 'ArrowUp') { event.preventDefault(); moveActive(-1); }
    else if (event.key === 'Home') { event.preventDefault(); setActiveIndex(enabledIndexes[0] ?? -1); }
    else if (event.key === 'End') { event.preventDefault(); setActiveIndex(enabledIndexes.at(-1) ?? -1); }
    else if (event.key === 'Escape') { event.preventDefault(); close({ focusTrigger: true }); }
    else if (['Enter', ' '].includes(event.key) && activeIndex >= 0) {
      event.preventDefault();
      select(options[activeIndex].props.value);
    }
  };

  return (
    <div ref={ref} className="relative w-full">
      {label && <span id={labelId} className="mb-2 block text-xs uppercase tracking-[0.24em] text-muted">{label}</span>}
      <button
        {...props}
        ref={triggerRef}
        type="button"
        role="combobox"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-labelledby={label ? labelId : undefined}
        aria-required={required || undefined}
        aria-invalid={error ? true : undefined}
        onClick={() => !disabled && setOpen((current) => !current)}
        onKeyDown={handleKeyDown}
        className={`flex w-full items-center justify-between rounded-2xl border border-line bg-surface px-4 py-3 text-left text-base text-ink outline-none transition focus:ring-2 focus:ring-accent disabled:opacity-50 ${className} ${error ? '!border-red-500 !bg-red-500/5 !ring-2 !ring-red-500/30' : ''}`}
      >
        <span className={`truncate ${hasValue ? '' : 'text-muted'}`}>{selected ? selected.props.children : options[0]?.props.children}</span>
        <ChevronDown size={16} className={`ml-2 shrink-0 text-muted transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && !disabled && (
        <div
          ref={listRef}
          id={listId}
          role="listbox"
          tabIndex={-1}
          aria-labelledby={label ? labelId : undefined}
          aria-label={label ? undefined : (props['aria-label'] || name || 'Выбор значения')}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-option-${activeIndex}` : undefined}
          onKeyDown={handleKeyDown}
          className="absolute z-50 mt-2 w-full overflow-hidden rounded-2xl border border-line bg-surface p-1 shadow-glass outline-none"
        >
          <div className="max-h-64 overflow-y-auto">
            {options.map((option, index) => {
              const active = index === activeIndex;
              const selectedOption = String(option.props.value) === String(value);
              return (
                <button
                  id={`${listId}-option-${index}`}
                  key={String(option.props.value)}
                  type="button"
                  role="option"
                  tabIndex={-1}
                  aria-selected={selectedOption}
                  disabled={option.props.disabled}
                  onClick={() => select(option.props.value)}
                  className={`flex w-full items-center justify-between rounded-xl px-3 py-2.5 text-left text-sm transition ${selectedOption ? 'bg-accent/10 font-medium text-accent' : 'text-ink hover:bg-base'} ${active && !selectedOption ? 'bg-base' : ''} disabled:opacity-50`}
                >
                  <span>{option.props.children}</span>{selectedOption && <Check size={14} />}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {error && <p className="mt-2 text-sm text-accent">{error}</p>}
    </div>
  );
};

export default Select;
